import React, { useEffect, useState } from "react";
import api from "../lib/api";



/* ─── types ───────────────────────────────────────────────── */
interface Tenant {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone_number: string | null;
  unit_number: string;
  property_name: string;
  meter_serial: string | null;
  balance_units: number;
  total_paid: number;
  last_payment: string | null;
  valve_open: boolean;
  meter_status: "online" | "offline" | "fault" | null;
}

/* ─── mock tenants (for UI demo when API not wired) ───────── */
const MOCK_TENANTS: Tenant[] = [
  { id: 1, first_name: "John", last_name: "Mwangi", username: "jmwangi", email: "john@example.com", phone_number: "0712345678", unit_number: "A-101", property_name: "Sunrise Apartments", meter_serial: "MTR-001", balance_units: 42.5, total_paid: 8500, last_payment: new Date(Date.now() - 3 * 86400000).toISOString(), valve_open: true, meter_status: "online" },
  { id: 2, first_name: "Grace", last_name: "Wanjiku", username: "gwanjiku", email: "grace@example.com", phone_number: "0723456789", unit_number: "A-202", property_name: "Sunrise Apartments", meter_serial: "MTR-003", balance_units: 8.2, total_paid: 6200, last_payment: new Date(Date.now() - 10 * 86400000).toISOString(), valve_open: true, meter_status: "online" },
  { id: 3, first_name: "Peter", last_name: "Otieno", username: "potieno", email: "peter@example.com", phone_number: "0734567890", unit_number: "B-104", property_name: "Westview Estate", meter_serial: "MTR-007", balance_units: 0, total_paid: 4300, last_payment: new Date(Date.now() - 25 * 86400000).toISOString(), valve_open: false, meter_status: "online" },
  { id: 4, first_name: "Mary", last_name: "Kamau", username: "mkamau", email: "mary@example.com", phone_number: "0745678901", unit_number: "C-301", property_name: "Greenpark", meter_serial: "MTR-012", balance_units: 3.1, total_paid: 12000, last_payment: new Date(Date.now() - 5 * 86400000).toISOString(), valve_open: true, meter_status: "fault" },
  { id: 5, first_name: "David", last_name: "Njoroge", username: "dnjoroge", email: "david@example.com", phone_number: "0756789012", unit_number: "B-205", property_name: "Sunrise Apartments", meter_serial: "MTR-005", balance_units: 67.8, total_paid: 9700, last_payment: new Date(Date.now() - 1 * 86400000).toISOString(), valve_open: true, meter_status: "online" },
  { id: 6, first_name: "Ann", last_name: "Chebet", username: "achebet", email: "ann@example.com", phone_number: "0767890123", unit_number: "A-103", property_name: "Westview Estate", meter_serial: "MTR-009", balance_units: 22.0, total_paid: 3800, last_payment: new Date(Date.now() - 7 * 86400000).toISOString(), valve_open: true, meter_status: "offline" },
  { id: 7, first_name: "Samuel", last_name: "Kariuki", username: "skariuki", email: "samuel@example.com", phone_number: null, unit_number: "D-402", property_name: "Greenpark", meter_serial: null, balance_units: 0, total_paid: 0, last_payment: null, valve_open: false, meter_status: null },
];

/* ─── helpers ─────────────────────────────────────────────── */
const fmt = (n: number, d = 0) =>
  n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

function initials(t: Tenant) {
  return `${t.first_name[0]}${t.last_name[0]}`.toUpperCase();
}

const METER_STATUS_META = {
  online:  { dot: "bg-emerald-400", label: "Online",  text: "text-emerald-600", bg: "bg-emerald-50" },
  offline: { dot: "bg-gray-400",    label: "Offline", text: "text-gray-500",    bg: "bg-gray-100" },
  fault:   { dot: "bg-red-400",     label: "Fault",   text: "text-red-500",     bg: "bg-red-50" },
};

/* ─── tenant detail drawer ───────────────────────────────── */
function TenantDrawer({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const balancePct = Math.min(100, (tenant.balance_units / 100) * 100);
  const balanceColor = tenant.balance_units <= 5 ? "bg-red-400" : tenant.balance_units <= 20 ? "bg-amber-400" : "bg-emerald-400";
  const meterMeta = tenant.meter_status ? METER_STATUS_META[tenant.meter_status] : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto flex flex-col">

        {/* header */}
        <div className="bg-gradient-to-r from-[#0f2557] to-[#0e2050] px-6 py-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sky-300/70 text-[12px] font-semibold uppercase tracking-widest">Tenant Profile</p>
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-xl font-bold text-white shadow-lg flex-shrink-0">
              {initials(tenant)}
            </div>
            <div>
              <p className="text-white font-bold text-[17px] leading-tight">{tenant.first_name} {tenant.last_name}</p>
              <p className="text-sky-300/70 text-[12.5px] mt-0.5">@{tenant.username}</p>
              <p className="text-sky-300/50 text-[12px] mt-0.5">{tenant.property_name} · Unit {tenant.unit_number}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 space-y-5">

          {/* contact */}
          <div className="bg-gray-50 rounded-2xl p-4 space-y-2.5">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Contact</p>
            {[
              { icon: "📧", label: "Email", value: tenant.email },
              { icon: "📱", label: "Phone", value: tenant.phone_number ?? "Not set" },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-3 text-[13px]">
                <span>{c.icon}</span>
                <span className="text-gray-400 w-12 flex-shrink-0">{c.label}</span>
                <span className="font-medium text-gray-800 truncate">{c.value}</span>
              </div>
            ))}
          </div>

          {/* balance */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-widest">Balance</p>
              <span className={`text-[12px] font-bold px-2.5 py-1 rounded-lg ${tenant.balance_units <= 5 ? "bg-red-50 text-red-500" : tenant.balance_units <= 20 ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                {tenant.balance_units <= 5 ? "⚠️ Low" : tenant.balance_units <= 20 ? "ℹ️ Moderate" : "✅ Good"}
              </span>
            </div>
            <p className="text-[28px] font-bold text-gray-800 leading-tight">{fmt(tenant.balance_units, 1)} <span className="text-[14px] text-gray-400 font-medium">units</span></p>
            <div className="mt-3 w-full bg-gray-100 rounded-full h-2">
              <div className={`h-2 rounded-full ${balanceColor} transition-all`} style={{ width: `${balancePct}%` }} />
            </div>
          </div>

          {/* meter */}
          {tenant.meter_serial ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Meter</p>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[14px] font-bold text-gray-800 font-mono">{tenant.meter_serial}</p>
                  {meterMeta && (
                    <span className={`inline-flex items-center gap-1.5 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${meterMeta.bg} ${meterMeta.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meterMeta.dot}`}/>
                      {meterMeta.label}
                    </span>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-xl ${tenant.valve_open ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                  {tenant.valve_open ? "💧 Valve Open" : "🔴 Valve Closed"}
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-4 text-center text-[13px] text-gray-400">
              No meter assigned to this unit yet.
            </div>
          )}

          {/* payment stats */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Total Paid", value: `KES ${fmt(tenant.total_paid)}`, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Last Payment", value: tenant.last_payment ? new Date(tenant.last_payment).toLocaleDateString("en-KE", { dateStyle: "medium" }) : "Never", color: "text-sky-600", bg: "bg-sky-50" },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} rounded-2xl p-4`}>
                <p className="text-[10.5px] text-gray-400 uppercase tracking-widest font-medium">{s.label}</p>
                <p className={`text-[15px] font-bold ${s.color} mt-1 leading-tight`}>{s.value}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─── tenant row ─────────────────────────────────────────── */
function TenantRow({ tenant, onClick }: { tenant: Tenant; onClick: () => void }) {
  const balanceColor = tenant.balance_units <= 5 ? "text-red-500" : tenant.balance_units <= 20 ? "text-amber-600" : "text-emerald-600";
  const balanceBg   = tenant.balance_units <= 5 ? "bg-red-50"    : tenant.balance_units <= 20 ? "bg-amber-50"    : "bg-emerald-50";
  const meterMeta   = tenant.meter_status ? METER_STATUS_META[tenant.meter_status] : null;

  return (
    <tr
      onClick={onClick}
      className="hover:bg-sky-50/40 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
    >
      {/* name + unit */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0">
            {initials(tenant)}
          </div>
          <div>
            <p className="text-[13px] font-semibold text-gray-800">{tenant.first_name} {tenant.last_name}</p>
            <p className="text-[11.5px] text-gray-400">@{tenant.username}</p>
          </div>
        </div>
      </td>
      {/* property & unit */}
      <td className="px-5 py-4">
        <p className="text-[13px] text-gray-700 font-medium">{tenant.property_name}</p>
        <p className="text-[11.5px] text-gray-400">Unit {tenant.unit_number}</p>
      </td>
      {/* meter */}
      <td className="px-5 py-4">
        {tenant.meter_serial ? (
          <div>
            <p className="text-[13px] font-mono text-gray-700">{tenant.meter_serial}</p>
            {meterMeta && (
              <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md mt-0.5 ${meterMeta.bg} ${meterMeta.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${meterMeta.dot}`} />
                {meterMeta.label}
              </span>
            )}
          </div>
        ) : (
          <span className="text-[12px] text-gray-300 font-medium">No meter</span>
        )}
      </td>
      {/* balance */}
      <td className="px-5 py-4">
        <span className={`text-[13px] font-bold ${balanceColor} ${balanceBg} px-2.5 py-1 rounded-lg`}>
          {fmt(tenant.balance_units, 1)} units
        </span>
      </td>
      {/* valve */}
      <td className="px-5 py-4">
        <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full ${tenant.valve_open ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tenant.valve_open ? "bg-emerald-400" : "bg-gray-300"}`} />
          {tenant.valve_open ? "Open" : "Closed"}
        </span>
      </td>
      {/* total paid */}
      <td className="px-5 py-4">
        <p className="text-[13px] font-semibold text-gray-800">KES {fmt(tenant.total_paid)}</p>
        <p className="text-[11.5px] text-gray-400">
          {tenant.last_payment ? new Date(tenant.last_payment).toLocaleDateString("en-KE", { dateStyle: "medium" }) : "No payments"}
        </p>
      </td>
      {/* chevron */}
      <td className="px-5 py-4 text-right">
        <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} className="w-4 h-4 ml-auto">
          <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>(MOCK_TENANTS);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "low" | "empty" | "good">("all");
  const [selected, setSelected] = useState<Tenant | null>(null);

  useEffect(() => {
    // attempt real API; fall back to mock silently
    api.get(`/accounts/tenants/`)
      .then((r) => { if ((r.data?.results ?? r.data ?? []).length) setTenants(r.data?.results ?? r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const properties = ["all", ...Array.from(new Set(tenants.map((t) => t.property_name)))];

  const filtered = tenants.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      `${t.first_name} ${t.last_name}`.toLowerCase().includes(q) ||
      t.username.toLowerCase().includes(q) ||
      t.email.toLowerCase().includes(q) ||
      (t.phone_number ?? "").includes(q) ||
      t.unit_number.toLowerCase().includes(q) ||
      (t.meter_serial ?? "").toLowerCase().includes(q);
    const matchProp = propertyFilter === "all" || t.property_name === propertyFilter;
    const matchStatus =
      statusFilter === "all" ? true :
      statusFilter === "empty" ? t.balance_units === 0 :
      statusFilter === "low"   ? t.balance_units > 0 && t.balance_units <= 10 :
      statusFilter === "good"  ? t.balance_units > 10 : true;
    return matchSearch && matchProp && matchStatus;
  });

  const totalTenants = tenants.length;
  const lowBalance   = tenants.filter((t) => t.balance_units <= 10).length;
  const emptyBalance = tenants.filter((t) => t.balance_units === 0).length;
  const valveOpen    = tenants.filter((t) => t.valve_open).length;
  const totalRevenue = tenants.reduce((s, t) => s + t.total_paid, 0);

  const STATUS_TABS = [
    { key: "all",   label: "All Tenants", count: totalTenants },
    { key: "low",   label: "Low Balance", count: lowBalance },
    { key: "empty", label: "Empty",       count: emptyBalance },
    { key: "good",  label: "Healthy",     count: tenants.filter((t) => t.balance_units > 10).length },
  ] as const;

  return (
    <>
      <div className="space-y-7">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Tenants</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">
              {totalTenants} tenants across all properties
            </p>
          </div>
          {emptyBalance > 0 && (
            <span className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-[12.5px] font-semibold px-4 py-2.5 rounded-xl">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              {emptyBalance} tenant{emptyBalance > 1 ? "s" : ""} out of water
            </span>
          )}
        </div>

        {/* ── KPI strip ── */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Tenants",   value: totalTenants.toString(),         sub: "Registered",           color: "bg-sky-50",     stroke: "#0ea5e9" },
            { label: "Low Balance",     value: lowBalance.toString(),           sub: "≤ 10 units left",       color: "bg-amber-50",   stroke: "#f59e0b" },
            { label: "Empty Balance",   value: emptyBalance.toString(),         sub: "Water cut off",         color: "bg-red-50",     stroke: "#ef4444" },
            { label: "Valves Open",     value: valveOpen.toString(),            sub: "Currently flowing",     color: "bg-emerald-50", stroke: "#10b981" },
            { label: "Total Revenue",   value: `KES ${fmt(totalRevenue)}`,     sub: "All-time payments",     color: "bg-violet-50",  stroke: "#6366f1" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={s.stroke} strokeWidth={1.8} className="w-5 h-5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[10.5px] text-gray-400 uppercase tracking-widest font-medium truncate">{s.label}</p>
                <p className="text-[16px] font-bold text-gray-800 leading-tight">{s.value}</p>
                <p className="text-[11px] text-gray-400 truncate">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── alert banner ── */}
        {lowBalance > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth={1.8} className="w-4.5 h-4.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div>
              <p className="text-[13px] font-bold text-amber-800">{lowBalance} tenant{lowBalance > 1 ? "s" : ""} with critically low or empty water balance</p>
              <p className="text-[12.5px] text-amber-700/80 mt-0.5">
                These tenants risk water interruption. Consider sending a top-up reminder via SMS.
              </p>
            </div>
          </div>
        )}

        {/* ── filters row ── */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* search */}
          <div className="relative flex-1">
            <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone, unit, meter…"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300 bg-white shadow-sm"
            />
          </div>
          {/* property filter */}
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] font-medium text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
          >
            {properties.map((p) => (
              <option key={p} value={p}>{p === "all" ? "All Properties" : p}</option>
            ))}
          </select>
        </div>

        {/* balance status tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold transition-all ${statusFilter === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {t.key === "low" && <span className="w-2 h-2 rounded-full bg-amber-400" />}
              {t.key === "empty" && <span className="w-2 h-2 rounded-full bg-red-400" />}
              {t.key === "good" && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
              {t.label}
              <span className={`text-[11px] px-1.5 py-0.5 rounded-md font-semibold ${statusFilter === t.key ? "bg-gray-100 text-gray-600" : "bg-gray-200 text-gray-500"}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {/* ── table ── */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gray-50 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={1.5} className="w-7 h-7">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="font-semibold text-gray-700 text-[14px]">No tenants found</p>
            <p className="text-[12.5px] text-gray-400 mt-1">Try adjusting your search or filter.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {["Tenant", "Property & Unit", "Meter", "Balance", "Valve", "Payments", ""].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-[11px] text-gray-400 font-semibold uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <TenantRow key={t.id} tenant={t} onClick={() => setSelected(t)} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/50 text-[12px] text-gray-400 flex items-center justify-between">
              <span>{filtered.length} tenant{filtered.length !== 1 ? "s" : ""}</span>
              <span>Click any row to view full profile →</span>
            </div>
          </div>
        )}

      </div>

      {/* ── drawer ── */}
      {selected && <TenantDrawer tenant={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
